import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';
import { Shield, AlertTriangle, Activity, Clock, Ban, CheckCircle, RefreshCw, Loader2, Search } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const SecurityDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);
  const [rateLimitConfig, setRateLimitConfig] = useState({
    submissions_per_hour: 10,
    submissions_per_day: 50,
    login_attempts_per_hour: 5,
    enabled: true
  });
  const [saving, setSaving] = useState(false);
  const [actionFilter, setActionFilter] = useState('all');

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    loadLogs();
  }, [logsPage, actionFilter]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [statsRes, configRes] = await Promise.all([
        axios.get(`${API_URL}/api/security/stats`, { headers: getAuthHeader() }),
        axios.get(`${API_URL}/api/security/rate-limit-config`, { headers: getAuthHeader() })
      ]);
      setStats(statsRes.data);
      setRateLimitConfig(configRes.data);
    } catch (error) {
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  };

  const loadLogs = async () => {
    try {
      const params = { page: logsPage, per_page: 20 };
      if (actionFilter !== 'all') {
        params.action = actionFilter;
      }
      const res = await axios.get(`${API_URL}/api/security/audit-logs`, { 
        headers: getAuthHeader(),
        params 
      });
      setLogs(res.data.items);
      setLogsTotal(res.data.total);
    } catch (error) {
      console.error('Error loading logs:', error);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/security/rate-limit-config`, rateLimitConfig, { headers: getAuthHeader() });
      toast.success('Konfiguration gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const handleClearIP = async (ip) => {
    try {
      await axios.post(`${API_URL}/api/security/clear-blocked-ip?ip=${encodeURIComponent(ip)}`, {}, { headers: getAuthHeader() });
      toast.success(`IP ${ip} entsperrt`);
      loadData();
    } catch (error) {
      toast.error('Fehler beim Entsperren');
    }
  };

  const getActionBadge = (action) => {
    const badges = {
      'bot_blocked': { color: 'bg-red-100 text-red-700', label: 'Bot blockiert' },
      'rate_limited': { color: 'bg-amber-100 text-amber-700', label: 'Rate Limited' },
      'login': { color: 'bg-blue-100 text-blue-700', label: 'Login' },
      'form_create': { color: 'bg-emerald-100 text-emerald-700', label: 'Formular erstellt' },
      'form_update': { color: 'bg-emerald-100 text-emerald-700', label: 'Formular aktualisiert' },
      'form_publish': { color: 'bg-purple-100 text-purple-700', label: 'Veröffentlicht' },
      'submission_create': { color: 'bg-cyan-100 text-cyan-700', label: 'Einreichung' },
      'settings_update': { color: 'bg-gray-100 text-gray-700', label: 'Einstellungen' },
    };
    const badge = badges[action] || { color: 'bg-gray-100 text-gray-700', label: action };
    return <Badge className={badge.color}>{badge.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Shield className="h-8 w-8 text-blue-600" />
            Security Dashboard
          </h1>
          <p className="text-gray-500 mt-1">Überwachung und Konfiguration der Sicherheitsfunktionen</p>
        </div>
        <Button onClick={loadData} variant="outline" size="sm">
          <RefreshCw className="h-4 w-4 mr-2" />
          Aktualisieren
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="border-l-4 border-l-red-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Ban className="h-4 w-4" />
              Blockierte Bots
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-red-600">{stats?.blocked_bots_today || 0}</div>
            <p className="text-xs text-gray-500">Heute</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-amber-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Rate Limited
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-amber-600">{stats?.rate_limited_today || 0}</div>
            <p className="text-xs text-gray-500">Heute</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-orange-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <AlertTriangle className="h-4 w-4" />
              Fehlgeschlagene Logins
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-orange-600">{stats?.failed_logins_today || 0}</div>
            <p className="text-xs text-gray-500">Heute</p>
          </CardContent>
        </Card>

        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-600 flex items-center gap-2">
              <Activity className="h-4 w-4" />
              Einreichungen
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-3xl font-bold text-emerald-600">{stats?.total_submissions_today || 0}</div>
            <p className="text-xs text-gray-500">Heute</p>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="overview">
        <TabsList>
          <TabsTrigger value="overview">Übersicht</TabsTrigger>
          <TabsTrigger value="audit">Audit Trail</TabsTrigger>
          <TabsTrigger value="config">Konfiguration</TabsTrigger>
        </TabsList>

        {/* Overview Tab */}
        <TabsContent value="overview" className="mt-6 space-y-6">
          {/* Suspicious IPs */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Verdächtige IP-Adressen</CardTitle>
              <CardDescription>IPs mit mehrfachen Rate-Limit-Verstößen</CardDescription>
            </CardHeader>
            <CardContent>
              {stats?.suspicious_ips?.length > 0 ? (
                <div className="space-y-2">
                  {stats.suspicious_ips.map((ip, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-red-50 rounded-lg border border-red-200">
                      <div className="flex items-center gap-3">
                        <AlertTriangle className="h-5 w-5 text-red-500" />
                        <code className="font-mono text-sm">{ip}</code>
                      </div>
                      <Button 
                        size="sm" 
                        variant="outline" 
                        onClick={() => handleClearIP(ip)}
                        className="text-emerald-600 border-emerald-300 hover:bg-emerald-50"
                      >
                        <CheckCircle className="h-4 w-4 mr-1" />
                        Entsperren
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                  <p>Keine verdächtigen IP-Adressen erkannt</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Security Status */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Sicherheitsstatus</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-medium">Honeypot aktiv</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-medium">Rate Limiting aktiv</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-emerald-50 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-emerald-600" />
                  <span className="text-sm font-medium">Bot-Schutz aktiv</span>
                </div>
                <div className="flex items-center gap-2 p-3 bg-gray-50 rounded-lg">
                  <Clock className="h-5 w-5 text-gray-400" />
                  <span className="text-sm font-medium text-gray-500">Turnstile (bald)</span>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Audit Trail Tab */}
        <TabsContent value="audit" className="mt-6">
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>Audit Trail</CardTitle>
                  <CardDescription>Alle Systemaktivitäten protokolliert</CardDescription>
                </div>
                <Select value={actionFilter} onValueChange={setActionFilter}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="Filter nach Aktion" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Alle Aktionen</SelectItem>
                    <SelectItem value="bot_blocked">Bot blockiert</SelectItem>
                    <SelectItem value="rate_limited">Rate Limited</SelectItem>
                    <SelectItem value="login">Login</SelectItem>
                    <SelectItem value="submission_create">Einreichung</SelectItem>
                    <SelectItem value="form_create">Formular erstellt</SelectItem>
                    <SelectItem value="settings_update">Einstellungen</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {logs.length > 0 ? (
                  <>
                    {logs.map((log, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 border rounded-lg hover:bg-gray-50">
                        <div className="flex items-center gap-3">
                          {getActionBadge(log.action)}
                          <div>
                            <p className="text-sm font-medium">
                              {log.user_name || log.ip_address || 'System'}
                            </p>
                            {log.resource_name && (
                              <p className="text-xs text-gray-500">{log.resource_name}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-gray-500">
                            {new Date(log.timestamp).toLocaleString('de-DE')}
                          </p>
                          {log.ip_address && (
                            <code className="text-xs text-gray-400">{log.ip_address}</code>
                          )}
                        </div>
                      </div>
                    ))}
                    
                    {/* Pagination */}
                    <div className="flex items-center justify-between pt-4">
                      <p className="text-sm text-gray-500">
                        {logsTotal} Einträge gesamt
                      </p>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={logsPage <= 1}
                          onClick={() => setLogsPage(p => p - 1)}
                        >
                          Zurück
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={logs.length < 20}
                          onClick={() => setLogsPage(p => p + 1)}
                        >
                          Weiter
                        </Button>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8 text-gray-500">
                    <Search className="h-12 w-12 mx-auto mb-3 text-gray-300" />
                    <p>Keine Einträge gefunden</p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Config Tab */}
        <TabsContent value="config" className="mt-6">
          <Card>
            <CardHeader>
              <CardTitle>Rate Limiting Konfiguration</CardTitle>
              <CardDescription>Schutz vor Missbrauch durch Anfragelimits</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between py-3 border-b">
                <div>
                  <Label className="text-base">Rate Limiting aktiviert</Label>
                  <p className="text-sm text-gray-500">Begrenzt Anfragen pro IP-Adresse</p>
                </div>
                <Switch
                  checked={rateLimitConfig.enabled}
                  onCheckedChange={(checked) => setRateLimitConfig(prev => ({ ...prev, enabled: checked }))}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div>
                  <Label>Einreichungen pro Stunde</Label>
                  <Input
                    type="number"
                    value={rateLimitConfig.submissions_per_hour}
                    onChange={(e) => setRateLimitConfig(prev => ({ ...prev, submissions_per_hour: parseInt(e.target.value) || 10 }))}
                    min={1}
                    max={100}
                  />
                  <p className="text-xs text-gray-500 mt-1">Max. Einreichungen pro IP/Stunde</p>
                </div>

                <div>
                  <Label>Einreichungen pro Tag</Label>
                  <Input
                    type="number"
                    value={rateLimitConfig.submissions_per_day}
                    onChange={(e) => setRateLimitConfig(prev => ({ ...prev, submissions_per_day: parseInt(e.target.value) || 50 }))}
                    min={1}
                    max={500}
                  />
                  <p className="text-xs text-gray-500 mt-1">Max. Einreichungen pro IP/Tag</p>
                </div>

                <div>
                  <Label>Login-Versuche pro Stunde</Label>
                  <Input
                    type="number"
                    value={rateLimitConfig.login_attempts_per_hour}
                    onChange={(e) => setRateLimitConfig(prev => ({ ...prev, login_attempts_per_hour: parseInt(e.target.value) || 5 }))}
                    min={1}
                    max={20}
                  />
                  <p className="text-xs text-gray-500 mt-1">Max. Login-Versuche pro IP/Stunde</p>
                </div>
              </div>

              <div className="flex justify-end">
                <Button onClick={handleSaveConfig} disabled={saving}>
                  {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                  Konfiguration speichern
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Honeypot Info */}
          <Card className="mt-6">
            <CardHeader>
              <CardTitle>Honeypot-Schutz</CardTitle>
              <CardDescription>Unsichtbare Felder zur Bot-Erkennung</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5" />
                  <div>
                    <p className="font-medium text-emerald-800">Honeypot ist automatisch aktiv</p>
                    <p className="text-sm text-emerald-600 mt-1">
                      Unsichtbare Felder werden automatisch zu allen öffentlichen Formularen hinzugefügt. 
                      Bots, die diese Felder ausfüllen, werden stillschweigend blockiert.
                    </p>
                    <p className="text-xs text-emerald-500 mt-2">
                      Erkannte Honeypot-Felder: <code>website</code>, <code>url</code>, <code>homepage</code>, <code>fax_number</code>
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default SecurityDashboard;
